import React, { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useUpdateCppPartner } from '~/hooks/apis/costPerPoint';

import type { CppPartner, UpdateCppPartnerBody } from '~/types/costPerPoint';

import BooleanMark from './BooleanMark';

interface Props {
  partner: CppPartner | null;
  /** When false the dialog is a read-only view: no inputs, no Save. */
  canEdit: boolean;
  onClose: () => void;
}

/** The three fields the service lets an admin change; everything else is view-only. */
interface BillingForm {
  legalName: string;
  address: string;
  gstPercentage: string;
}

/** A read-only label/value pair. */
function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </Box>
  );
}

/** Renders a value, or a muted dash when it is unset. */
function OrDash({ value }: { value: string | number | null | undefined }) {
  if (value === null || value === undefined || value === '') {
    return (
      <Box component="span" color="text.disabled">
        —
      </Box>
    );
  }
  return <>{value}</>;
}

/** Seeds the editable fields from the partner. */
function toForm(partner: CppPartner | null): BillingForm {
  return {
    legalName: partner?.legalName ?? '',
    address: partner?.address ?? '',
    gstPercentage: partner?.gstPercentage != null ? String(partner.gstPercentage) : '',
  };
}

/**
 * Partner detail dialog: every field the directory holds, plus the billing details the invoice
 * depends on.
 *
 * Editing is gated on the caller's role. Without it the dialog is a plain read-only view; with it
 * the three fields the service accepts — legal entity name, address and GST rate — become inputs
 * with a Save. Everything else (limits, permission flags, webhook) stays read-only for everyone,
 * because `PATCH /partners/v1/partners/:partnerId` does not accept those fields at all.
 */
export default function PartnerDetailsDialog({ partner, canEdit, onClose }: Props) {
  const [form, setForm] = useState<BillingForm>(() => toForm(partner));
  const updateMutation = useUpdateCppPartner();

  // Reseed whenever a different partner is opened, so the previous partner's edits never leak in.
  useEffect(() => {
    setForm(toForm(partner));
  }, [partner]);

  if (!partner) {
    return null;
  }

  const gstNumber = Number(form.gstPercentage);
  const isGstValid = form.gstPercentage !== '' && Number.isFinite(gstNumber) && gstNumber >= 0 && gstNumber <= 100;

  // A field that held a value and is now blank cannot be saved: the endpoint has no way to express
  // "clear this". Saying so beats silently dropping the edit and reporting success.
  const clearedFields = [
    ...(partner.legalName && !form.legalName.trim() ? ['legal entity name'] : []),
    ...(partner.address && !form.address.trim() ? ['billing address'] : []),
  ];
  const hasSomethingToSend = Boolean(form.legalName.trim() || form.address.trim() || isGstValid);
  const saveBlockedReason = clearedFields.length
    ? `${clearedFields.join(' and ')} cannot be cleared through this form — ask a developer to unset it`
    : !hasSomethingToSend
      ? 'Enter at least one billing detail to save'
      : form.gstPercentage !== '' && !isGstValid
        ? 'Enter a GST rate between 0 and 100'
        : '';

  const handleSave = async () => {
    // The service accepts these three fields only, and each as `string | undefined` — there is no
    // way to send a cleared value, so a blank is omitted rather than sent as an empty string.
    const body: UpdateCppPartnerBody = {
      ...(form.legalName.trim() && { legalName: form.legalName.trim() }),
      ...(form.address.trim() && { address: form.address.trim() }),
      ...(isGstValid && { gstPercentage: gstNumber }),
    };

    try {
      await updateMutation.mutateAsync({ partnerId: partner.id, body });
      onClose();
    } catch {
      // The hook raises the error toast; swallowing the rejection here keeps the dialog open on
      // the failed values instead of closing over them, and avoids an unhandled rejection.
    }
  };

  return (
    <Dialog open onClose={updateMutation.isPending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="overline" color="text.secondary" display="block">
          Partner
        </Typography>
        <Typography variant="h6" component="div">
          {partner.name}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="overline" color="text.secondary">
          Billing details
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          Used on the partner invoice.
        </Typography>

        {canEdit ? (
          <Stack spacing={2} mt={1}>
            <TextField
              size="small"
              label="Legal entity name"
              value={form.legalName}
              onChange={(event) => setForm((previous) => ({ ...previous, legalName: event.target.value }))}
              placeholder={partner.name}
              helperText="Falls back to the trading name on the invoice when empty"
              fullWidth
            />
            <TextField
              size="small"
              label="Billing address"
              value={form.address}
              onChange={(event) => setForm((previous) => ({ ...previous, address: event.target.value }))}
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label="GST rate"
              value={form.gstPercentage}
              onChange={(event) => setForm((previous) => ({ ...previous, gstPercentage: event.target.value }))}
              inputProps={{ step: 0.01, min: 0, max: 100 }}
              error={form.gstPercentage !== '' && !isGstValid}
              helperText={
                form.gstPercentage !== '' && !isGstValid ? 'Enter a percentage between 0 and 100' : 'Percent, e.g. 10'
              }
              sx={{ width: 200 }}
            />
          </Stack>
        ) : (
          <Stack spacing={1.5} mt={1}>
            <Detail label="Legal entity name">
              <OrDash value={partner.legalName} />
            </Detail>
            <Detail label="Billing address">
              <OrDash value={partner.address} />
            </Detail>
            <Detail label="GST rate">
              {partner.gstPercentage != null ? `${partner.gstPercentage}%` : <OrDash value={null} />}
            </Detail>
          </Stack>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="overline" color="text.secondary">
          Configuration
        </Typography>
        {canEdit && (
          <Typography variant="caption" color="text.secondary" display="block">
            Managed outside this dialog.
          </Typography>
        )}

        <Box
          mt={1.5}
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }}
          gap={2}
        >
          <Detail label="Partner type">{partner.partnerType}</Detail>
          <Detail label="Status">
            <Chip size="small" variant="outlined" label={partner.status} />
          </Detail>
          <Detail label="Points per transaction">
            {partner.minPointsPerTransaction} – {partner.maxPointsPerTransaction}
          </Detail>
          <Detail label="Status credits per transaction">
            {partner.minStatusCreditsPerTransaction} – {partner.maxStatusCreditsPerTransaction}
          </Detail>
          <Detail label="Bonus points per transaction">
            {partner.minBonusPointsPerTransaction} – {partner.maxBonusPointsPerTransaction}
          </Detail>
          <Detail label="Daily points limit">
            <OrDash value={partner.dailyPointsLimit} />
          </Detail>
          <Detail label="Monthly points limit">
            <OrDash value={partner.monthlyPointsLimit} />
          </Detail>
          <Detail label="Max requests / minute">
            <OrDash value={partner.maxRequestsPerMinute} />
          </Detail>
          <Detail label="Max requests / hour">
            <OrDash value={partner.maxRequestsPerHour} />
          </Detail>
          <Detail label="Webhook URL">
            <OrDash value={partner.webhookUrl} />
          </Detail>
        </Box>

        <Box
          mt={2.5}
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }}
          gap={1.5}
        >
          <Detail label="Credit points">
            <BooleanMark value={partner.allowCreditPoints} />
          </Detail>
          <Detail label="Credit status credits">
            <BooleanMark value={partner.allowCreditStatusCredits} />
          </Detail>
          <Detail label="Credit bonus points">
            <BooleanMark value={partner.allowCreditBonusPoints} />
          </Detail>
          <Detail label="Debit points">
            <BooleanMark value={partner.allowDebitPoints} />
          </Detail>
          <Detail label="Debit status credits">
            <BooleanMark value={partner.allowDebitStatusCredits} />
          </Detail>
          <Detail label="Debit bonus points">
            <BooleanMark value={partner.allowDebitBonusPoints} />
          </Detail>
          <Detail label="Refund">
            <BooleanMark value={partner.allowRefund} />
          </Detail>
          <Detail label="Tier upgrade">
            <BooleanMark value={partner.allowTierUpgrade} />
          </Detail>
          <Detail label="Require name match">
            <BooleanMark value={partner.requireNameMatch} />
          </Detail>
          <Detail label="Fail fast">
            <BooleanMark value={partner.failFast} />
          </Detail>
        </Box>

        {!canEdit && (
          <Alert severity="info" sx={{ mt: 3 }}>
            You need the loyalty admin role to edit a partner&apos;s billing details.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={updateMutation.isPending}>
          {canEdit ? 'Cancel' : 'Close'}
        </Button>
        {canEdit && saveBlockedReason && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', ml: 1 }}>
            {saveBlockedReason}
          </Typography>
        )}
        {canEdit && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={updateMutation.isPending || Boolean(saveBlockedReason)}
          >
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
